import React from 'react';
import { useQuery } from 'react-query';
import { gamingAPI } from '../services/api';
import { fetchBootProgress, floorBootView } from '../utils/bootProgressQuery';
import { ExternalLink, Copy, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import WolfPinLink from './WolfPinLink';

const playConnectCacheKey = (sessionId) => `levelup.playConnect.${sessionId}`;

const readPlayConnectCache = (sessionId) => {
  if (!sessionId) return undefined;
  try {
    const raw = sessionStorage.getItem(playConnectCacheKey(sessionId));
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
};

const writePlayConnectCache = (sessionId, payload) => {
  if (!sessionId || !payload) return;
  if (!payload.tailscale_ip && !payload.public_ip && !payload.browser?.public_ip) return;
  try {
    const { player_auth_key, player_up_command, ...safe } = payload;
    sessionStorage.setItem(
      playConnectCacheKey(sessionId),
      JSON.stringify({ ...safe, cachedAt: Date.now() })
    );
  } catch {
    /* ignore quota / private mode */
  }
};

const usablePlayConnectCache = (sessionId) => {
  const cached = readPlayConnectCache(sessionId);
  if (!cached) return undefined;
  const hasIp = cached.tailscale_ip || cached.public_ip || cached.browser?.public_ip;
  if (!hasIp) return undefined;
  const age = Date.now() - (cached.cachedAt || 0);
  if (age > 5000 && !(cached.tailscale_ip && cached.public_ip && cached.play_ready)) {
    return undefined;
  }
  return cached;
};

const seedFromSession = (session) => {
  if (!session) return undefined;
  const playUrl = session.play_url;
  const desktopUrl = session.desktop_url;
  if (!session.tailscale_ip && !session.public_ip && !playUrl) return undefined;
  return {
    tailscale_ip: session.tailscale_ip || null,
    public_ip: session.public_ip || null,
    hostname: session.hostname || session.tailscale_info?.hostname || null,
    display_name: session.display_name || null,
    session_id: session.db_id || null,
    browser: {
      ready: !!playUrl,
      url: playUrl || null,
      views: {
        wolf: { ready: !!playUrl, url: playUrl || null },
        desktop: { ready: !!desktopUrl, url: desktopUrl || null },
      },
    },
  };
};

const BOOT_STEPS = [
  { id: 'reserved', label: 'Reserving a GPU', pct: 8 },
  { id: 'booting', label: 'Starting the machine', pct: 20 },
  { id: 'online', label: 'Machine is online', pct: 32 },
  { id: 'network', label: 'Opening the stream ports', pct: 45 },
  { id: 'install', label: 'Installing the stream server', pct: 60 },
  { id: 'platform', label: 'Starting Airpulse Game', pct: 75 },
  { id: 'pairing', label: 'Connecting your session', pct: 85 },
  { id: 'vpn', label: 'Joining VPN (Moonlight on PC)', pct: 92 },
  { id: 'tiles', label: 'Preparing game tiles', pct: 100 },
];

const deriveBoot = (session, play, apiBoot) => {
  const publicIp = play?.public_ip || play?.browser?.public_ip || session?.public_ip;
  const streamUp = !!(
    apiBoot?.browser_ready ||
    apiBoot?.ready ||
    play?.heal_ready ||
    play?.play_ready
  );
  const wgUp = !!(
    play?.wireguard_ready ||
    apiBoot?.is_wireguard_ready ||
    play?.is_wireguard_ready
  );
  const status = String(session?.status || '').toLowerCase();
  const running = status === 'running' || status === 'success';
  const starting = ['pending', 'starting', 'creating', 'loading'].includes(status);
  let percent = 8;
  if (starting || running || publicIp) percent = 20;
  if (running && publicIp) percent = 32;
  if (publicIp) percent = Math.max(percent, 32);
  const apiPct = parseInt(apiBoot?.percent, 10);
  if (Number.isFinite(apiPct) && apiPct > 0) percent = Math.max(percent, apiPct);
  if (streamUp) percent = Math.max(percent, apiBoot?.tailscale_ready || wgUp ? 100 : 92);
  else percent = Math.min(percent, 85);
  percent = Math.max(0, Math.min(100, percent));
  const sessionKey = session?.db_id || session?.id || session?.instance_id;
  const floored = floorBootView(sessionKey, { ...apiBoot, percent, current: apiBoot?.current, steps: apiBoot?.steps, failed: apiBoot?.failed }, percent);
  percent = floored.percent;
  const ready = !!(apiBoot?.ready || streamUp);
  const current =
    floored.current ||
    apiBoot?.current ||
    (ready
      ? (play?.tailscale_ip || wgUp ? 'Ready to play' : 'Browser ready — VPN still joining')
      : (BOOT_STEPS.slice().reverse().find((s) => percent >= s.pct) || BOOT_STEPS[0]).label);
  const sourceSteps = floored.steps?.length ? floored.steps : (apiBoot?.steps?.length ? apiBoot.steps : BOOT_STEPS);
  const vpnDone = !!(play?.tailscale_ip || wgUp);
  const steps = sourceSteps.map((step) => {
    if (step.status) {
      if (step.id === 'vpn') {
        return { ...step, status: vpnDone ? 'done' : (step.status === 'done' ? 'active' : step.status) };
      }
      return step;
    }
    if (step.id === 'vpn') {
      return { ...step, status: vpnDone ? 'done' : 'active' };
    }
    return {
      ...step,
      status: ready || percent >= step.pct ? 'done' : percent >= (step.pct || 0) - 12 ? 'active' : 'pending',
    };
  });
  return { percent, ready, current, steps };
};

const ProgressTrack = ({ done, label }) => (
  <div className="mt-2">
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
      <div
        className={`h-full rounded-full transition-all duration-500 ${
          done ? 'w-full bg-emerald-400' : 'w-2/3 bg-sky-400 animate-pulse'
        }`}
      />
    </div>
    <p className="mt-1 text-[11px] text-white/50">{done ? 'Ready' : label}</p>
  </div>
);

const NasLibraryCard = ({ iscsi }) => {
  if (!iscsi?.enabled) return null;
  const pct = Math.max(0, Math.min(100, Number(iscsi.percent) || 0));
  const ready = !!iscsi.ready;
  const failed = iscsi.status === 'error' || !!iscsi.error;
  const width = ready ? 100 : Math.max(6, pct);
  return (
    <div className="rounded-md bg-black/30 p-3 space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-white/80 text-sm font-medium">NAS library</p>
        <p className={`text-xs font-mono ${ready ? 'text-emerald-300' : failed ? 'text-red-300' : 'text-sky-300'}`}>
          {ready ? 'Ready' : `${pct}%`}
        </p>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-white/10">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            failed ? 'bg-red-400' : ready ? 'bg-emerald-400' : 'bg-sky-400'
          }`}
          style={{ width: `${width}%` }}
        />
      </div>
      <p className="text-xs text-white/70">
        {iscsi.label || (ready ? 'NAS library ready' : 'Preparing extra disk…')}
      </p>
      <p className="text-[11px] text-white/50">
        Extra disk only — Steam on the GPU keeps going. Games copy to the NAS after format finishes.
      </p>
    </div>
  );
};

const GameConnectPanel = ({ sessionId, session }) => {
  const view = 'wolf';

  const cachedConnect = usablePlayConnectCache(sessionId) || seedFromSession(session);

  const bootId = session?.db_id || sessionId;
  const { data: boot } = useQuery(
    ['bootProgress', bootId],
    () => fetchBootProgress(bootId),
    {
      enabled: !!bootId,
      staleTime: 0,
      keepPreviousData: true,
      refetchInterval: (latest) =>
        latest?.failed || (latest?.ready && latest?.tailscale_ready) ? 8000 : 2000,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true,
    }
  );

  const { data, refetch } = useQuery(
    ['playConnect', sessionId, view],
    () => gamingAPI.getPlayConnect(sessionId, view),
    {
      enabled: !!sessionId,
      placeholderData: cachedConnect,
      staleTime: 0,
      cacheTime: 15_000,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchIntervalInBackground: true,
      refetchInterval: (latest) => {
        if (latest?.iscsi?.enabled && !latest.iscsi.ready) {
          return 2000;
        }
        if (latest?.heal_ready || latest?.play_ready) {
          return latest?.tailscale_ip ? 8000 : 4000;
        }
        return 2000;
      },
      onSuccess: (payload) => writePlayConnectCache(sessionId, payload),
    }
  );

  const dbId = data?.session_id || session?.db_id;
  const fallbackPlay = session?.play_url || (dbId ? `https://play.airpulse.net/s/${dbId}/` : null);

  const browser = data?.browser || {};
  const views = browser.views || {};
  const tsIp = data?.tailscale_ip || session?.tailscale_ip;
  const wgHost = data?.moonlight_wg_host;
  const moonlightHost = wgHost || tsIp;
  const publicIp = data?.public_ip || data?.browser?.public_ip || session?.public_ip;
  const playUrl = views.wolf?.url || browser.url || session?.play_url || fallbackPlay;
  const downloads = data?.downloads || {
    tailscale: 'https://tailscale.com/download',
    moonlight: 'https://github.com/moonlight-stream/moonlight-qt/releases/latest',
    wireguard: 'https://www.wireguard.com/install/',
  };

  const copy = async (text, label) => {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const openBrowser = () => {
    // Must call window.open inside the click. Waiting on /play-connect
    // makes the browser treat this as a popup and block it — that is
    // why the button sat on "Opening…" with no new tab.
    const url = playUrl;
    if (!url) {
      toast.error('Play URL is not ready yet — wait a moment.');
      refetch().catch(() => {});
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
    refetch().catch(() => {});
  };

  const playMoonlight = async () => {
    const latest = (await refetch()).data;
    const wgHost = latest?.moonlight_wg_host;
    const ts = latest?.tailscale_ip;
    const ip = wgHost || ts;
    if (!ip) {
      toast.error(
        'Moonlight host is not ready yet. Import Play on PC, TV, or box, or wait for Tailscale 100.x.',
      );
      return;
    }
    await copy(
      ip,
      wgHost ? 'Moonlight host (WireGuard 10.88.0.1)' : 'Tailscale IP',
    );
  };

  const playSeats = Array.isArray(session?.play_seats) ? session.play_seats : (data?.play_seats || []);
  const familySeats = playSeats.length > 1;
  const playName = data?.display_name || 'Play';
  const derived = deriveBoot(session, data, boot);
  const bootFailed = !!boot?.failed;
  const bootPct = derived.percent;
  const bootReady = derived.ready && !bootFailed;
  const canOpenPlay = !bootFailed && !!playUrl && !!(data?.heal_ready || data?.play_ready);
  const playLabel = !canOpenPlay
    ? `Preparing… ${bootPct}%`
    : `Play ${playName} in browser`;

  return (
    <div className="rounded-lg border border-sky-400/50 bg-sky-950/30 p-4 space-y-4">
      <h3 className="text-white font-semibold text-lg">Play</h3>
      {bootFailed && (
        <div className="rounded-lg border border-red-400/60 bg-red-950/50 p-3 space-y-1">
          <p className="text-red-100 font-semibold">This GPU did not start</p>
          <p className="text-sm text-red-100/90">
            {boot?.detail
              || 'The rented machine failed before Airpulse could start. We closed it so you are not billed while it sits broken. Pick another GPU.'}
          </p>
        </div>
      )}
      <p className="text-sm text-white/70">
        {familySeats
          ? 'One GPU. Browser Play is a single stream — one person. Everyone else (or all four) uses the Moonlight app. One browser + three Moonlight clients is OK.'
          : 'One GPU for games. Play in the browser or in the Moonlight app — Steam, RetroArch, Lutris, Pegasus, and Prism Launcher.'}
      </p>
      <p className="text-xs text-white/60">
        Play in browser opens your session. Steam, RetroArch, Lutris, Pegasus, and Prism Launcher are the tiles — click Start.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={openBrowser}
          disabled={!canOpenPlay}
          className="inline-flex flex-col items-stretch gap-2 rounded-lg bg-sky-500 hover:bg-sky-400 disabled:bg-sky-950 disabled:text-white/50 disabled:cursor-not-allowed text-white font-semibold px-4 py-3"
        >
          <span className="inline-flex items-center justify-center gap-2">
            <ExternalLink className="h-5 w-5" />
            {playLabel}
          </span>
          {!bootReady && (
            <span className="h-1.5 overflow-hidden rounded-full bg-black/25">
              <span
                className="block h-full rounded-full bg-white/90 transition-all duration-500"
                style={{ width: `${Math.max(6, bootPct)}%` }}
              />
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={playMoonlight}
          disabled={!moonlightHost}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-950 disabled:text-white/50 text-white font-semibold px-4 py-3"
        >
          <Copy className="h-5 w-5" />
          {wgHost
            ? 'Play in Moonlight — copy 10.88.0.1'
            : tsIp
              ? 'Play in Moonlight — copy Tailscale IP'
              : 'Moonlight waiting for VPN IP'}
        </button>
      </div>
      {familySeats && view === 'wolf' && (
        <div className="rounded-md bg-black/30 p-3 space-y-2">
          <p className="text-white/80 text-sm font-medium">Family — who can play at once</p>
          <ul className="text-xs text-white/70 space-y-1 list-disc pl-4">
            <li>Moonlight app: up to four people at the same time (host 10.88.0.1 below).</li>
            <li>Browser: one person. Do not open four Play tabs — they fight over the same stream.</li>
            <li>Mix: one browser + three Moonlight is the full house.</li>
          </ul>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
            {playSeats.map((seat) => (
              <div
                key={seat.seat}
                className={`rounded-md px-3 py-2 text-sm text-left ${
                  seat.mine
                    ? 'bg-sky-500/80 text-white'
                    : 'bg-white/10 text-white/80'
                }`}
              >
                <p className="font-semibold">
                  {seat.label || `Seat ${seat.seat}`}
                  {seat.mine ? ' — you' : ''}
                </p>
                <p className="text-[11px] text-white/70 mt-0.5">
                  {seat.mine
                    ? 'You may use browser Play or Moonlight. If you take the browser, the others must use Moonlight.'
                    : 'Simultaneous play: Moonlight app only. Browser is already one shared seat.'}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      {Array.isArray(derived.steps) && derived.steps.length > 0 && (
        <ol className="space-y-1 text-xs">
          {derived.steps.map((step) => (
            <li
              key={step.id}
              className={
                step.status === 'done'
                  ? 'text-emerald-300'
                  : step.status === 'active'
                    ? 'text-white font-medium'
                    : 'text-white/40'
              }
            >
              {step.status === 'done' ? '✓' : step.status === 'active' ? '●' : '○'} {step.label}
            </li>
          ))}
        </ol>
      )}

      <p className="text-xs text-white/60">
        Browser play uses the public IP and does not wait for a VPN. Moonlight
        on your PC can use WireGuard host 10.88.0.1 (Profile: Play on PC, TV, or
        box) or Tailscale 100.x as fallback. Game UDP is not on the public IP.
        The wallet charges from the moment Vast reserves the GPU — including
        boot — because Vast bills us then. Solo pays the full box. Family uses
        the shared master wallet. Party / friendship splits that same hour
        across the people who started. After Play is ready, time the machine
        is on stays billed by the second.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-md bg-black/30 p-3 text-sm space-y-1">
          <p className="text-white/80 font-medium">Moonlight — WireGuard</p>
          <p className={`font-mono text-xl break-all ${wgHost ? 'text-emerald-300' : 'text-white/50'}`}>
            {wgHost || '10.88.0.1 when GPU VPN is up'}
          </p>
          <p className="text-xs text-white/50">
            Import Play on PC, TV, or box from Profile, then paste this host in Moonlight.
          </p>
          <ProgressTrack
            done={!!wgHost}
            label={wgHost ? 'WireGuard on the GPU is listening' : 'Waiting for GPU VPN'}
          />
        </div>
        <div className="rounded-md bg-black/30 p-3 text-sm space-y-1">
          <p className="text-white/80 font-medium">Moonlight — Tailscale fallback</p>
          <p className={`font-mono text-xl break-all ${tsIp ? 'text-emerald-300' : 'text-white/50'}`}>
            {tsIp || 'Waiting for 100.x'}
          </p>
          {data?.hostname && (
            <p className="text-xs font-mono text-white/50">{data.hostname}</p>
          )}
          <ProgressTrack
            done={!!tsIp}
            label="GPU is joining Tailscale — used if WireGuard Moonlight is not up yet"
          />
        </div>
        <div className="rounded-md bg-black/30 p-3 text-sm space-y-1">
          <p className="text-white/80 font-medium">Browser — public IP</p>
          <p className={`font-mono text-xl break-all ${publicIp ? 'text-sky-300' : 'text-white/50'}`}>
            {publicIp || 'Waiting for public IP'}
          </p>
          <p className="text-xs text-white/50">
            Do not paste this into Moonlight. Video UDP is not published on this address.
          </p>
          <ProgressTrack
            done={!!publicIp}
            label={publicIp ? 'Public IP is up — Play in browser uses this path' : 'Waiting for the host to publish the IP…'}
          />
        </div>
      </div>
      <NasLibraryCard iscsi={data?.iscsi} />
      {!bootReady && (
        <p className="text-xs text-amber-200/90">
          Play in browser unlocks when the stream is actually up — not when the GPU first gets an IP.
          Moonlight can use WireGuard 10.88.0.1 or Tailscale 100.x.
        </p>
      )}

      {browser.message && (
        <p className="text-xs text-amber-200/90">{browser.message}</p>
      )}

      <div className="flex flex-wrap gap-2">
        <a
          href={downloads.tailscale}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-md border border-white/20 px-3 py-2 text-sm text-white hover:bg-white/10"
        >
          <Download className="h-4 w-4" />
          Download Tailscale
        </a>
        <a
          href={downloads.moonlight}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-md border border-white/20 px-3 py-2 text-sm text-white hover:bg-white/10"
        >
          <Download className="h-4 w-4" />
          Download Moonlight
        </a>
        <a
          href={downloads.wireguard || 'https://www.wireguard.com/install/'}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-md border border-white/20 px-3 py-2 text-sm text-white hover:bg-white/10"
        >
          <Download className="h-4 w-4" />
          Download WireGuard
        </a>
      </div>

      <ol className="text-xs text-white/70 list-decimal pl-4 space-y-1">
        <li>
          Play in browser: click Play. The stream opens as https://play.airpulse.net/s/… (HTTPS). Do not type your Airpulse Game email on that page. Steam, RetroArch, Lutris, Pegasus, and Prism Launcher are the tiles — click Start.
          {familySeats ? ' Only one person can use the browser at a time.' : ''}
        </li>
        <li>
          Moonlight: Profile → Play on PC, TV, or box, then host 10.88.0.1. Tailscale 100.x stays as fallback. Pair with the 4-digit PIN.
          {familySeats
            ? ' Up to four Moonlight clients at once, or one browser plus three Moonlight.'
            : ''}
        </li>
        <li>Use 1080p / 60 fps / 15–25 Mbps. Tailscale exit node: None.</li>
      </ol>

      {data?.player_up_command && (
        <div className="rounded-md border border-white/10 p-3">
          <p className="text-xs text-white/70 mb-2">
            Connect Tailscale for this session only. This is not the Airpulse Game admin login — you cannot see or change our password.
          </p>
          <button
            type="button"
            onClick={() => copy(data.player_up_command, 'Tailscale connect command')}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm px-3 py-2"
          >
            <Copy className="h-4 w-4" />
            Copy Tailscale connect command
          </button>
        </div>
      )}
      {data?.player_key_error && (
        <p className="text-xs text-amber-200/80">
          Session Tailscale join key is not available yet. Install Tailscale, then use the 100.x IP once it appears. Admin: add tag:levelup-player to the OAuth client and paste the updated ACL.
        </p>
      )}

      <WolfPinLink
        sessionId={sessionId}
        moonlightHost={moonlightHost}
        moonlightVia={data?.moonlight_via}
      />
    </div>
  );
};

export default GameConnectPanel;
